import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Header from "@user/components/Header";
import Footer from "@user/components/Footer";
import { AccountDangerZone } from "@/components/AccountDangerZone";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageSquare, Users, User as UserIcon, Calendar, ArrowRight, Pencil, Save, X, AlertCircle, FileText } from "lucide-react";
import { toast } from "sonner";
import UpcomingConsultations, { CurrentConsultations } from "@user/components/UpcomingConsultations";
import RecordsPrivacyPanel from "@user/components/RecordsPrivacyPanel";

const statusColor = {
  pending: "bg-amber-50 text-amber-800",
  accepted: "bg-blue-50 text-blue-800",
  completed: "bg-emerald-50 text-emerald-800",
  rejected: "bg-red-50 text-red-800",
};

function profileCompleteness(user) {
  const fields = ["name", "email", "phone", "city", "preferred_language"];
  const filled = fields.filter((f) => (user?.[f] || "").toString().trim().length > 0);
  return { filled: filled.length, total: fields.length, missing: fields.filter((f) => !(user?.[f] || "").toString().trim().length) };
}

export default function Dashboard() {
  const { user, setUser, loading } = useAuth();
  const [chats, setChats] = useState([]);
  const [consults, setConsults] = useState([]);
  const [recordsCount, setRecordsCount] = useState(0);
  const [pendingAccess, setPendingAccess] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const initialTab = searchParams.get("tab") || "chats";
  const [tab, setTab] = useState(initialTab);

  useEffect(() => {
    if (!user && !loading) navigate("/");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get("/chats").then((r) => setChats(r.data)).catch(() => {});
    api.get("/consultations").then((r) => setConsults(r.data)).catch(() => {});
    api.get("/me/consultation-records").then((r) => setRecordsCount((r.data.items || []).length)).catch(() => {});
    api.get("/me/lcr-access-requests?state=pending").then((r) => setPendingAccess((r.data.items || []).length)).catch(() => {});
  }, [user]);

  const applyRescheduled = (u) => setConsults((prev) => prev.map((c) => (c.id === u.id ? { ...c, ...u } : c)));
  const discardConsult = async (cid) => {
    try {
      const r = await api.post(`/consultations/${cid}/discard`);
      setConsults((prev) => prev.map((c) => (c.id === cid ? { ...c, ...r.data } : c)));
      toast.success("Incomplete booking discarded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not discard this booking");
    }
  };

  // Sync tab with URL
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && t !== tab) setTab(t);
  }, [searchParams, tab]);

  const onTabChange = (t) => {
    setTab(t);
    setSearchParams(t === "chats" ? {} : { tab: t });
  };

  const completeness = useMemo(() => profileCompleteness(user), [user]);
  const profileIncomplete = user && completeness.filled < completeness.total;

  if (loading || !user) {
    return <div className="min-h-screen grid place-items-center"><div className="h-10 w-10 rounded-full border-4 border-slate-200 border-t-[#1E3A8A] animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <div className="flex items-center gap-4">
          {user.picture ? (
            <img src={user.picture} alt="" className="h-14 w-14 rounded-2xl" />
          ) : (
            <div className="h-14 w-14 rounded-2xl adv-navy text-[#D4AF37] grid place-items-center font-bold text-xl">{(user.name || "?")[0]}</div>
          )}
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
              Namaste, {(user.name || "there").split(" ")[0]} 👋
            </h1>
            <p className="text-slate-500 text-sm mt-1">{user.email || user.phone || ""}</p>
          </div>
        </div>

        {/* Profile-incomplete nudge */}
        {profileIncomplete && tab !== "profile" && (
          <div
            data-testid="profile-incomplete-banner"
            className="mt-6 flex items-center gap-3 rounded-2xl border border-[#D4AF37]/40 bg-gradient-to-r from-[#FBF4DF] to-white px-4 py-3"
          >
            <div className="h-9 w-9 rounded-xl adv-navy text-[#D4AF37] grid place-items-center shrink-0">
              <AlertCircle size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-[#0A2540]">Complete your profile for better lawyer matching</p>
              <p className="text-xs text-slate-600">
                {completeness.filled}/{completeness.total} fields filled · add {completeness.missing.map((m) => m.replace("_", " ")).join(", ")}
              </p>
            </div>
            <Button
              onClick={() => onTabChange("profile")}
              data-testid="profile-incomplete-cta"
              className="rounded-full adv-btn-gold font-bold text-xs h-9 px-4"
            >
              Complete now
            </Button>
          </div>
        )}

        <div className="grid md:grid-cols-4 gap-4 mt-6">
          <Stat icon={MessageSquare} label="Saved chats" value={chats.length} to="/chat" />
          <Stat icon={Users} label="Consultations" value={consults.length} onClick={() => onTabChange("consults")} />
          <Stat icon={FileText} label="Records" value={recordsCount} to="/my-records" />
          <Stat icon={Calendar} label="Pending" value={consults.filter((c) => c.status === "pending").length} onClick={() => onTabChange("consults")} />
        </div>

        <section className="mt-8" data-testid="dashboard-current-consultations">
          <h2 className="text-base md:text-lg font-extrabold text-[#0A2540] mb-3" style={{ fontFamily: "Poppins" }}>Your consultations</h2>
          <CurrentConsultations consults={consults} onViewAll={() => onTabChange("consults")} onDiscard={discardConsult} onRescheduled={applyRescheduled} />
        </section>

        <Tabs value={tab} onValueChange={onTabChange} className="mt-8">
          <TabsList data-testid="dashboard-tabs">
            <TabsTrigger value="chats" data-testid="tab-chats">Saved Chats</TabsTrigger>
            <TabsTrigger value="consults" data-testid="tab-consults">Consultations</TabsTrigger>
            <TabsTrigger value="records-privacy" data-testid="tab-records-privacy" className="relative">
              Records & Privacy
              {pendingAccess > 0 && (
                <span className="ml-2 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold">
                  {pendingAccess}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
          </TabsList>

          <TabsContent value="chats" className="mt-4">
            <div className="bg-white border border-slate-200 rounded-2xl divide-y">
              {chats.length === 0 && (
                <div className="p-8 text-center text-slate-500">
                  No saved chats yet. <Link to="/chat" className="text-[#1E3A8A] font-semibold">Ask a question →</Link>
                </div>
              )}
              {chats.map((c) => (
                <Link key={c.id} to={`/chat/${c.id}`} className="flex items-center gap-3 p-4 hover:bg-slate-50" data-testid={`dashboard-chat-${c.id}`}>
                  <MessageSquare size={16} className="text-[#1E3A8A]" />
                  <span className="flex-1 text-sm font-medium text-slate-800 truncate">{c.title}</span>
                  <ArrowRight size={14} className="text-slate-400" />
                </Link>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="consults" className="mt-4" id="consults">
            <UpcomingConsultations consults={consults} onDiscard={discardConsult} onRescheduled={applyRescheduled} />
          </TabsContent>

          <TabsContent value="records-privacy" className="mt-4">
            <RecordsPrivacyPanel />
          </TabsContent>

          <TabsContent value="profile" className="mt-4">
            <ProfileCard user={user} setUser={setUser} />
          </TabsContent>
        </Tabs>

        <AccountDangerZone redirect="/" />
      </div>
      <Footer />
    </div>
  );
}

function ProfileCard({ user, setUser }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: user.name || "",
    email: user.email || "",
    phone: user.phone || "",
    preferred_language: user.preferred_language || "english",
    city: user.city || "",
  });

  const startEdit = () => {
    setForm({
      name: user.name || "",
      email: user.email || "",
      phone: user.phone || "",
      preferred_language: user.preferred_language || "english",
      city: user.city || "",
    });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/auth/profile", form);
      setUser(res.data);
      toast.success("Profile updated");
      setEditing(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to save";
      toast.error(typeof msg === "string" ? msg : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 max-w-2xl" data-testid="profile-card">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#DBEAFE] text-[#1E3A8A] grid place-items-center">
            <UserIcon size={20} />
          </div>
          <div>
            <h3 className="font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>My Profile</h3>
            <p className="text-xs text-slate-500 capitalize">{user.role}</p>
          </div>
        </div>
        {!editing ? (
          <Button
            onClick={startEdit}
            data-testid="edit-profile-btn"
            className="rounded-full adv-btn-primary font-semibold text-xs h-9 px-4"
          >
            <Pencil size={13} /> Edit Profile
          </Button>
        ) : (
          <Button
            variant="outline"
            onClick={() => setEditing(false)}
            data-testid="cancel-profile-btn"
            className="rounded-full font-semibold text-xs h-9 px-4"
          >
            <X size={13} /> Cancel
          </Button>
        )}
      </div>

      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <Field label="Full Name" value={form.name} editing={editing} onChange={(v) => setForm({ ...form, name: v })} placeholder="Your full name" testid="field-name" />
        <Field label="Email" value={form.email} editing={editing} onChange={(v) => setForm({ ...form, email: v })} placeholder="you@example.com" testid="field-email" type="email" />
        <Field label="Phone Number" value={form.phone} editing={editing} onChange={(v) => setForm({ ...form, phone: v })} placeholder="+91 98765 43210" testid="field-phone" type="tel" />
        <Field label="City / Location" value={form.city} editing={editing} onChange={(v) => setForm({ ...form, city: v })} placeholder="Mumbai, Delhi, etc." testid="field-city" />
        <div>
          <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500">Preferred Language</label>
          {editing ? (
            <Select value={form.preferred_language} onValueChange={(v) => setForm({ ...form, preferred_language: v })}>
              <SelectTrigger className="mt-1 h-10" data-testid="field-language">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="english">English</SelectItem>
                <SelectItem value="hindi">हिंदी / Hindi</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <p className="mt-1 text-sm font-semibold text-slate-800 capitalize h-10 flex items-center">{form.preferred_language || "—"}</p>
          )}
        </div>
      </div>

      {editing && (
        <div className="mt-6 flex justify-end">
          <Button
            onClick={save}
            disabled={saving}
            data-testid="save-profile-btn"
            className="rounded-full adv-btn-gold font-bold text-sm h-10 px-6 disabled:opacity-60"
          >
            <Save size={14} /> {saving ? "Saving…" : "Save Changes"}
          </Button>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, editing, onChange, placeholder, testid, type = "text" }) {
  return (
    <div>
      <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</label>
      {editing ? (
        <Input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={testid}
          className="mt-1 h-10"
        />
      ) : (
        <p className="mt-1 text-sm font-semibold text-slate-800 h-10 flex items-center" data-testid={`${testid}-readonly`}>
          {value || <span className="text-slate-400 font-normal">Not set</span>}
        </p>
      )}
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, to, onClick }) => {
  const inner = (
    <>
      <div className="h-11 w-11 rounded-xl bg-[#FBF4DF] text-[#8a6d14] grid place-items-center">
        <Icon size={20} />
      </div>
      <div>
        <p className="text-2xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>{value}</p>
        <p className="text-xs text-slate-500 font-medium">{label}</p>
      </div>
    </>
  );
  const cls = "bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-md transition-all flex items-center gap-4 w-full text-left";
  if (onClick) return <button onClick={onClick} className={cls}>{inner}</button>;
  return <Link to={to} className={cls}>{inner}</Link>;
};
